import type {
  FramingBox,
  FramingCoordinateSpace,
  FramingTransform,
} from "../framingInspector";

/**
 * Coordinate-root-aware viewport math for the scene-forensics workbench
 * (CROP-WB-007). Cameras are normalized to a declared root so panes can only
 * synchronize through a deterministic, recorded transform chain — never
 * across a generative seam.
 */

export type FramingViewportCamera = {
  rootId: string;
  /** 0..1 in the declared root. */
  centerX: number;
  centerY: number;
  /** 1 = fit. */
  scale: number;
};

export const MIN_VIEWPORT_SCALE = 1;
export const MAX_VIEWPORT_SCALE = 32;

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export function fitCamera(rootId: string): FramingViewportCamera {
  return { rootId, centerX: 0.5, centerY: 0.5, scale: 1 };
}

/** Keep the visible window inside the media and the scale within limits. */
export function clampCamera(
  camera: FramingViewportCamera,
): FramingViewportCamera {
  const scale = clamp(
    Number.isFinite(camera.scale) ? camera.scale : 1,
    MIN_VIEWPORT_SCALE,
    MAX_VIEWPORT_SCALE,
  );
  const halfWindow = 0.5 / scale;
  return {
    rootId: camera.rootId,
    centerX: clamp(
      Number.isFinite(camera.centerX) ? camera.centerX : 0.5,
      halfWindow,
      1 - halfWindow,
    ),
    centerY: clamp(
      Number.isFinite(camera.centerY) ? camera.centerY : 0.5,
      halfWindow,
      1 - halfWindow,
    ),
    scale,
  };
}

/** Zoom keeping the given normalized focal point stationary. */
export function zoomCameraAt(
  camera: FramingViewportCamera,
  focalX: number,
  focalY: number,
  factor: number,
): FramingViewportCamera {
  const nextScale = clamp(
    camera.scale * factor,
    MIN_VIEWPORT_SCALE,
    MAX_VIEWPORT_SCALE,
  );
  const applied = nextScale / camera.scale;
  if (applied === 1) return clampCamera(camera);
  return clampCamera({
    rootId: camera.rootId,
    centerX: focalX - (focalX - camera.centerX) / applied,
    centerY: focalY - (focalY - camera.centerY) / applied,
    scale: nextScale,
  });
}

export function panCameraBy(
  camera: FramingViewportCamera,
  deltaX: number,
  deltaY: number,
): FramingViewportCamera {
  return clampCamera({
    ...camera,
    centerX: camera.centerX + deltaX,
    centerY: camera.centerY + deltaY,
  });
}

/**
 * Transform kinds that keep a deterministic pixel mapping between parent and
 * child. Anything generative (or unknown) breaks the chain: generated media
 * is a new coordinate root and must never be linked back to source space.
 */
const DETERMINISTIC_TRANSFORM_KINDS = new Set([
  "identity",
  "crop",
  "crop_resize",
  "resize",
  "contain",
  "pad",
  "exif_normalize",
  "orientation",
  "trim",
  "provider_input_crop",
]);

function isGenerativeSeam(
  space: FramingCoordinateSpace,
  transform: FramingTransform | null | undefined,
): boolean {
  const stage = (space.stage ?? "").toLowerCase();
  if (stage.includes("generated")) return true;
  const kind = (transform?.kind ?? "").toLowerCase();
  if (!kind) return false;
  return kind.includes("generat") || kind.includes("provider_output");
}

function isDeterministicTransform(
  transform: FramingTransform | null | undefined,
): boolean {
  const kind = (transform?.kind ?? "").toLowerCase();
  if (!kind) return false;
  return DETERMINISTIC_TRANSFORM_KINDS.has(kind);
}

type LineageStep = {
  space: FramingCoordinateSpace;
  transform: FramingTransform | null;
};

/**
 * Walk from a space to its deterministic root, returning the chain. Stops
 * (root reached) when there is no parent; returns null on a cycle.
 */
export function deterministicLineage(
  spaceId: string,
  spaces: readonly FramingCoordinateSpace[],
): LineageStep[] | null {
  const byId = new Map(spaces.map((space) => [space.id, space]));
  const chain: LineageStep[] = [];
  const seen = new Set<string>();
  let current = byId.get(spaceId);
  if (!current) return null;
  while (current) {
    if (seen.has(current.id)) return null; // cycle: fail closed
    seen.add(current.id);
    const transform = current.transformFromParent ?? null;
    if (isGenerativeSeam(current, transform)) {
      // The generative space itself is a root; nothing above it is reachable.
      chain.push({ space: current, transform: null });
      return chain;
    }
    chain.push({ space: current, transform });
    if (!current.parentSpaceId) return chain;
    if (transform && !isDeterministicTransform(transform)) {
      // Unknown transform: the chain above it cannot be trusted.
      return chain;
    }
    current = byId.get(current.parentSpaceId);
    if (current === undefined) return chain;
  }
  return chain;
}

export type LinkEligibility =
  | { linked: true; rootId: string }
  | { linked: false; reason: string };

/**
 * Two panes may synchronize only when both resolve to the same
 * deterministic root through recorded transforms (CROP-WB-007 invariant 3).
 */
export function linkEligibility(
  spaceIdA: string,
  spaceIdB: string,
  spaces: readonly FramingCoordinateSpace[],
): LinkEligibility {
  if (spaceIdA === spaceIdB) return { linked: true, rootId: spaceIdA };
  const lineageA = deterministicLineage(spaceIdA, spaces);
  const lineageB = deterministicLineage(spaceIdB, spaces);
  if (!lineageA || !lineageB) {
    return { linked: false, reason: "Coordinate lineage is unavailable." };
  }
  const rootA = lineageA.at(-1)!.space;
  const rootB = lineageB.at(-1)!.space;
  const generativeA = isGenerativeSeam(rootA, rootA.transformFromParent);
  const generativeB = isGenerativeSeam(rootB, rootB.transformFromParent);
  if (generativeA !== generativeB || rootA.id !== rootB.id) {
    if (generativeA || generativeB) {
      return {
        linked: false,
        reason:
          "Generated pixels form a new coordinate root; no measured mapping " +
          "back to the source exists.",
      };
    }
    return {
      linked: false,
      reason: "The panes do not share a recorded coordinate root.",
    };
  }
  const incomplete = [...lineageA, ...lineageB].some(
    (step) =>
      step.space.id !== rootA.id &&
      step.transform != null &&
      !isDeterministicTransform(step.transform),
  );
  if (incomplete) {
    return {
      linked: false,
      reason: "A transform in the lineage is not deterministically mapped.",
    };
  }
  return { linked: true, rootId: rootA.id };
}

/** Map a normalized point from a child space into its parent space. */
function pointToParent(
  x: number,
  y: number,
  transform: FramingTransform | null,
): { x: number; y: number } {
  if (!transform) return { x, y };
  const crop = transform.crop;
  if (crop) {
    return {
      x: crop.xMin + x * (crop.xMax - crop.xMin),
      y: crop.yMin + y * (crop.yMax - crop.yMin),
    };
  }
  const content = transform.contentBox;
  if (content) {
    // Contain/pad: the parent occupies contentBox inside the child canvas.
    const width = content.xMax - content.xMin;
    const height = content.yMax - content.yMin;
    if (width <= 0 || height <= 0) return { x, y };
    return {
      x: (x - content.xMin) / width,
      y: (y - content.yMin) / height,
    };
  }
  return { x, y };
}

/** Map a normalized point from a parent space into the child space. */
function pointFromParent(
  x: number,
  y: number,
  transform: FramingTransform | null,
): { x: number; y: number } {
  if (!transform) return { x, y };
  const crop = transform.crop;
  if (crop) {
    const width = crop.xMax - crop.xMin;
    const height = crop.yMax - crop.yMin;
    if (width <= 0 || height <= 0) return { x, y };
    return { x: (x - crop.xMin) / width, y: (y - crop.yMin) / height };
  }
  const content = transform.contentBox;
  if (content) {
    return {
      x: content.xMin + x * (content.xMax - content.xMin),
      y: content.yMin + y * (content.yMax - content.yMin),
    };
  }
  return { x, y };
}

/**
 * Project a camera defined in one space into another space that shares its
 * deterministic root. Returns null when the panes are not linkable.
 */
export function projectCameraBetweenSpaces(
  camera: FramingViewportCamera,
  fromSpaceId: string,
  toSpaceId: string,
  spaces: readonly FramingCoordinateSpace[],
): FramingViewportCamera | null {
  if (fromSpaceId === toSpaceId) return clampCamera(camera);
  const eligibility = linkEligibility(fromSpaceId, toSpaceId, spaces);
  if (!eligibility.linked) return null;
  const lineageFrom = deterministicLineage(fromSpaceId, spaces)!;
  const lineageTo = deterministicLineage(toSpaceId, spaces)!;
  let x = camera.centerX;
  let y = camera.centerY;
  for (const step of lineageFrom) {
    if (step.space.id === eligibility.rootId) break;
    ({ x, y } = pointToParent(x, y, step.transform));
  }
  const downward = [...lineageTo].filter(
    (step) => step.space.id !== eligibility.rootId,
  );
  for (const step of downward.reverse()) {
    ({ x, y } = pointFromParent(x, y, step.transform));
  }
  return clampCamera({ ...camera, rootId: toSpaceId, centerX: x, centerY: y });
}

export function normalizedBoxCenter(box: FramingBox): {
  x: number;
  y: number;
} {
  return { x: (box.xMin + box.xMax) / 2, y: (box.yMin + box.yMax) / 2 };
}
